// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { fetchTenantId } from 'apis/tenants';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';
import { selectTenants, setTenants } from 'redux/slices/siteInfoSlice';
import { selectUser, setUserAsAdminSite } from 'redux/slices/userSlice';
import { Path } from 'routes';
import { createAxios } from 'ts/createAxios';
import { DbManager } from 'util/DbManager';
import { API_ENDPOINTS } from 'util/endPoints';
import { TenantAuthen } from 'util/Enums';
import { Tenant } from 'util/Types';

export const useScanICBarcode = () => {
  const user = useSelector(selectUser);
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tenants = useSelector(selectTenants);

  const [isFetching, setIsFetching] = useState(false);

  const onScanFinish = async ({
    authenMethod,
    code,
    customCallback,
  }: {
    authenMethod: TenantAuthen;
    code: string;
    customCallback?: (newTenantId: string | undefined) => void;
  }) => {
    setIsFetching(true);

    const cachedSites = await DbManager.sites.toArray();
    if (authenMethod === TenantAuthen.ICCard && cachedSites.length) {
      const isSiteAdminCard = cachedSites.find((site) =>
        site.adminCards.find((card) => card.card === code)
      );
      if (isSiteAdminCard) {
        dispatch(setUserAsAdminSite());
        history.push(`${Path.selectTenant}`);
        toast.warning(t('messages.M_temporary_admin_site_login'));
        return;
      }
    }

    fetchTenantId({ authenMethod, code }, async (data) => {
      if (data.id != null) {
        let cachedTenants: Tenant[] | undefined = undefined;

        if (cachedSites.length) {
          cachedTenants = await DbManager.tenants.orderBy('name').toArray();
        }

        if (
          !(cachedTenants ?? tenants).find(({ id }) => id === Number(data.id))
        ) {
          await createAxios()
            .get(API_ENDPOINTS.TENANTS, {
              params: {
                siteId: user.siteId,
              },
            })
            .then(async (response) => {
              dispatch(setTenants(response.data.tenants));

              await DbManager.tenants.clear();
              await DbManager.tenants.bulkAdd(response.data.tenants);
            });
        }

        if (customCallback) {
          customCallback(data.id);
        } else {
          history.push(`${Path.emissionNew}?tenantId=${data.id}`);
        }
      }
    }).finally(() => {
      setIsFetching(false);
      if (authenMethod === TenantAuthen.Barcode) {
        const btnStart = document.querySelector(
          '#html5-qrcode-button-camera-start'
        ) as HTMLButtonElement;
        btnStart?.click();
      }
    });
  };

  const onNewScanBarcode = (decodedText: string) => {
    try {
      window.sendScannedText(decodedText);
      // eslint-disable-next-line no-empty
    } catch {}

    if (!isFetching) {
      onScanFinish({ authenMethod: TenantAuthen.Barcode, code: decodedText });
    }
  };

  const onNewScanICCard = (
    iccard: string,
    customCallback?: (newTenantId: string | undefined) => void
  ) => {
    try {
      window.sendScannedText(iccard);
      // eslint-disable-next-line no-empty
    } catch {}

    if (!isFetching) {
      onScanFinish({
        authenMethod: TenantAuthen.ICCard,
        code: iccard,
        customCallback,
      });
    }
  };

  return {
    isFetching,
    onNewScanBarcode,
    onNewScanICCard,
    onScanFinish,
  };
};
